import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { Tag } from '../components/Tag';
import {
  Campo,
  Carregando,
  ErroBox,
  Form,
  LinhaCampos,
  Modal,
  ModalRodape,
  PageHeader,
  Vazio,
} from '../components/ui';

interface Interno {
  id: string;
  name: string;
  email: string;
  status: string;
  lastLoginAt: string | null;
  createdAt: string;
  inviteExpiresAt: string | null;
}

interface RespostaConvite {
  user: Interno;
  inviteToken: string;
  inviteExpiresAt: string;
}

const CORES_STATUS: Record<string, string> = {
  ATIVO: 'success',
  CONVIDADO: 'warning',
  BLOQUEADO: 'danger',
  INATIVO: 'muted',
};

/**
 * Equipe MatériaPrimAi — as contas de plataforma (`organizationId` null).
 *
 * Não confundir com `/usuarios`, que é a equipe DE UM CLIENTE. São tabelas
 * diferentes de gente: quem está aqui enxerga todos os clientes.
 */
export function UsuariosInternos() {
  const cliente = useQueryClient();
  const [convidando, setConvidando] = useState(false);
  const [convidado, setConvidado] = useState<RespostaConvite | null>(null);

  const consulta = useQuery({
    queryKey: ['master', 'internos'],
    queryFn: () => api<Interno[]>('/master/internal-users'),
  });

  const convidar = useMutation({
    mutationFn: (dados: Record<string, string>) =>
      api<RespostaConvite>('/master/internal-users', {
        method: 'POST',
        body: { name: dados.name, email: dados.email },
      }),
    onSuccess: (resposta) => {
      cliente.invalidateQueries({ queryKey: ['master', 'internos'] });
      setConvidando(false);
      setConvidado(resposta);
    },
  });

  const alterarStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      api(`/master/internal-users/${id}`, { method: 'PATCH', body: { status } }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: ['master', 'internos'] }),
  });

  return (
    <>
      <PageHeader
        titulo="Usuários Internos"
        subtitulo="Equipe MatériaPrimAi. Estas contas enxergam todos os clientes."
        acoes={
          <button className="btn btn-primary" onClick={() => setConvidando(true)}>
            <i className="ti ti-user-plus" /> Convidar
          </button>
        }
      />

      {consulta.isPending && <Carregando />}
      {consulta.isError && <ErroBox erro={consulta.error} aoTentar={() => consulta.refetch()} />}

      {consulta.data &&
        (consulta.data.length === 0 ? (
          <Vazio icone="ti-users" titulo="Nenhuma conta interna" />
        ) : (
          <div className="card" style={{ padding: 0 }}>
            <div className="tabela-envolvente">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>E-mail</th>
                    <th>Último acesso</th>
                    <th>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {consulta.data.map((u) => (
                    <tr key={u.id}>
                      <td>
                        <div className="celula-principal">{u.name}</div>
                      </td>
                      <td>{u.email}</td>
                      <td>
                        {u.lastLoginAt
                          ? new Date(u.lastLoginAt).toLocaleString('pt-BR')
                          : <span className="text-dim">nunca entrou</span>}
                      </td>
                      <td>
                        <Tag type={CORES_STATUS[u.status] ?? 'muted'}>{u.status}</Tag>
                      </td>
                      <td>
                        <select
                          className="form-select"
                          style={{ width: 'auto', padding: '4px 8px', fontSize: 12 }}
                          value=""
                          onChange={(e) =>
                            e.target.value &&
                            alterarStatus.mutate({ id: u.id, status: e.target.value })
                          }
                        >
                          <option value="">Status…</option>
                          {['ATIVO', 'BLOQUEADO', 'INATIVO']
                            .filter((s) => s !== u.status)
                            .map((s) => (
                              <option key={s} value={s}>
                                {s}
                              </option>
                            ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}

      {alterarStatus.isError && (
        <div style={{ marginTop: 16 }}>
          <ErroBox erro={alterarStatus.error} />
        </div>
      )}

      {convidando && (
        <Modal
          titulo="Convidar para a equipe"
          descricao="A conta enxerga todos os clientes da plataforma. Convide com o mesmo critério de quem entrega a chave do escritório."
          aoFechar={() => setConvidando(false)}
          largura={520}
        >
          <Form aoEnviar={(dados) => convidar.mutate(dados)}>
            {convidar.isError && (
              <div className="form-erro">{(convidar.error as Error).message}</div>
            )}

            <LinhaCampos>
              <Campo label="Nome" name="name" obrigatorio autoFocus />
              <Campo label="E-mail" name="email" tipo="email" obrigatorio />
            </LinhaCampos>

            <ModalRodape>
              <button type="button" className="btn btn-ghost" onClick={() => setConvidando(false)}>
                Cancelar
              </button>
              <button className="btn btn-primary" type="submit" disabled={convidar.isPending}>
                {convidar.isPending ? 'Convidando…' : 'Convidar'}
              </button>
            </ModalRodape>
          </Form>
        </Modal>
      )}

      {convidado && (
        <Modal
          titulo="Convite criado"
          descricao="Este link aparece UMA vez. No banco fica só o hash — se fechar sem copiar, será preciso convidar de novo."
          aoFechar={() => setConvidado(null)}
          largura={560}
        >
          <div className="alert alert-success">
            <i className="ti ti-check" /> {convidado.user.name} · {convidado.user.email}
          </div>

          <div className="campo">
            <span className="campo-label">Link do convite</span>
            <input
              className="form-input"
              readOnly
              value={`${window.location.origin}/convite?token=${convidado.inviteToken}`}
              onFocus={(e) => e.currentTarget.select()}
            />
            <span className="campo-ajuda">
              Expira em {new Date(convidado.inviteExpiresAt).toLocaleString('pt-BR')}.
            </span>
          </div>

          <ModalRodape>
            <button className="btn btn-primary" onClick={() => setConvidado(null)}>
              Fechar
            </button>
          </ModalRodape>
        </Modal>
      )}
    </>
  );
}
